import { useState, useEffect, useRef } from 'react';
import api from '../api/axiosInstance';
import { PaginatedPipelines, PipelineData } from '../const/PipelineTypes';
import { useSwobNotifications } from './NotificationsProvider';
import { useWebSocketService } from './useWebSocketService';
import keycloak from '../services/keycloak';
import { AxiosError } from 'axios';
import { useLoader } from './useLoader';
import { LOADER_DEBOUNCE_MS } from '../const/global-constants';

type Notification = {
  title: string | HTMLElement;
  description?: string | HTMLElement;
  icon?: string;
  stripeColor?: string;
  action?: () => void;
  timeout?: number;
  isBanner?: boolean;
  timestamp: Date;
};

export const usePipelineData = (initialStatus?: string): PipelineData => {
  const [paginatedData, setPaginatedData] = useState<PaginatedPipelines>({ data: [], total: 0, limit: 0, offset: 0, nextPage: 0 });
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();
  const loaderTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Connect to WebSocket with the JWT token
  const ws = useWebSocketService();

  const areNotificationsEqual = (a: Notification, b: Notification) => {
    return a.title === b.title && a.icon === b.icon && a.description === b.description;
  };

  useEffect(() => {
    let ln: Notification = {
      title: '',
      icon: '',
      description: '',
      timestamp: new Date()
    };
    // Subscribe to workflow changes
    // Websocket functionality: only updates the websocket on the page.
    // If pipeline was added or deleted, user needs to refresh
    const unsubscribe = ws?.onEntityChange('workflow', (data) => {
      // Handle error
      if (!data) {
        addNotifications([
          {
            title: 'Websocket Error',
            icon: 'icon-triangle-warning',
            description: 'An error occurred with the websocket. ',
            timestamp: new Date()
          }
        ]);
        return;
      }
      // Handle different types of changes
      if (data.action === 'create') {
        const notification = {
          title: 'Workflow Added',
          description: `Workflow ${data.payload?.TicketId} was added.`,
          icon: 'info',
          timestamp: new Date()
        };
        if (areNotificationsEqual(ln, notification)) {
          return;
        } else {
          addNotifications([notification]);
          ln = { ...notification };
        }
      } else if (data.action === 'update') {
        // Validate payload has required fields before replacing row data
        if (!data.payload?.id || !data.payload?.TicketId) {
          return;
        }
        setPaginatedData((prev) => {
          const exists = prev.data.some((item) => item.id === data.payload.id);
          return exists ? { ...prev, data: prev.data.map((item) => (item.id === data.payload.id ? data.payload : item)) } : prev;
        });
        const notification = {
          title: 'Workflow Updated',
          description: `Workflow ${data.payload.TicketId} was updated to Stage ${data.payload?.currentStage} - Step ${data.payload?.currentStep} : ${data.payload?.status}`,
          icon: 'icon-triangle-warning',
          timestamp: new Date()
        };

        if (areNotificationsEqual(ln, notification)) {
          return;
        } else {
          addNotifications([notification]);
          ln = { ...notification };
        }
      } else if (data.action === 'delete') {
        // setPaginatedData(prev => ({ ...prev, data: prev.data.filter((item) => item.id !== data.payload?.id) }));
        const notification = {
          title: 'Workflow Deleted',
          description: `Pipeline ${data.payload?.TicketId} was deleted.`,
          icon: 'info',
          timestamp: new Date()
        };
        if (areNotificationsEqual(ln, notification)) {
          return;
        } else {
          addNotifications([notification]);
          ln = { ...notification };
        }
      } else if (data.action === 'status-change') {
        const notification = {
          title: 'Workflow Status Change',
          description: `Workflow ${data.payload?.ticketId} status changed to ${data.payload?.status}.`,
          icon: 'info',
          timestamp: new Date()
        };
        setPaginatedData((prev) => {
          const exists = prev.data.some((item) => item.id === data.payload.id);
          return exists ? { ...prev, data: prev.data.map((item) => (item.id === data.payload.id ? {...item, status: data.payload?.status} : item)) } : prev;
        });
        if (areNotificationsEqual(ln, notification)) {
          return;
        } else {
          addNotifications([notification]);
          ln = { ...notification };
        }
      }
    });

    const fetchPipelines = async () => {
      try {
        setIsLoading(true);
        showLoader();

        let url = '/api/workflows/paginated?limit=20&offset=0';
        if (initialStatus) url += `&status=${initialStatus}`;
        const resp = await api.get(url);
        setPaginatedData(resp.data);
      } catch (error: unknown) {
        // show error
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description:
              error && error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'An error occurred while fetching pipelines',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
        setError(error instanceof Error ? error.message : 'An error occurred while fetching pipelines');
        console.log(error);
      } finally {
        loaderTimer.current = setTimeout(() => {
          setIsLoading(false);
          hideLoader();
        }, LOADER_DEBOUNCE_MS);
      }
    };
    if (keycloak.authenticated) fetchPipelines();

    // Cleanup on component unmount
    return () => {
      unsubscribe();
      clearTimeout(loaderTimer.current);
      // wst.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { paginatedData, isLoading, error };
};
